import React, { useEffect, useMemo, useState } from "react";
import PermissionButton from "../shared/PermissionButton";
import Select from "../shared/Select";
import { useYearLevelByAssessmentSubjectConfig } from "../../hooks/useSubjectAssessment";
import {
  useSelectClassrooms,
  useStudents,
} from "../../hooks/useOptionalSubjects";
import {
  useGeneralAssessmentSelectByYearAndClass,
  useGeneralRecordedAssessment,
  useIsGeneralAssessmentFinalized,
} from "../../hooks/useGeneralAssessment";
import Button from "../shared/Button";
import { Spinner } from "reactstrap";
import CustomModal from "../shared/CustomModal";
import AssessmentConfigModal from "./AssessmentConfigModal";
import {
  checkRolePermissions,
  checkSubsTeacherPermissions,
} from "../../../../utils/Constants";
import { useARRCode } from "../../context/ARRLevelContext";

const GeneralAssessment = ({ name }) => {
  const { arrLevel } = useARRCode();
  const { yearLevel, yearLoading } =
    useYearLevelByAssessmentSubjectConfig(name);
  const { classrooms, classroomLoading, getClassrooms } = useSelectClassrooms();
  const {
    assessments,
    assessmentsLoading,
    generalAssessmentSelectByYearAndClass,
  } = useGeneralAssessmentSelectByYearAndClass();
  const {
    students,
    setStudents,
    getAllStudents,
    loading: isFetchingStudents,
  } = useStudents();
  const {
    finalized,
    loading: isFinalizing,
    updateAssessmentFinalized,
    getAssessmentFinalized: refetchFinalized,
  } = useIsGeneralAssessmentFinalized();

  const {
    recordedAssessment,
    recordedAssessmentLoading,
    selectGeneralRecordedAssessment,
    saveGeneralRecordedAssessment,
  } = useGeneralRecordedAssessment(refetchFinalized);

  const [formData, setFormData] = useState({});
  const [assessmentModal, setAssessmentModal] = useState(false);
  const [selectStudentId, setSelectedStudentId] = useState(null);
  const [permissions, setPermissions] = useState({
    finalizeGeneralPermission: false,
    unFinalizeGeneralPermission: false,
  });

  const yearLevelOptions = useMemo(() => {
    if (yearLevel?.length === 0) {
      return [];
    }

    return yearLevel?.map((item) => ({
      educationLevelId: item?.educationLevelId,
      label: item.name,
      value: item?.id,
    }));
  }, [yearLevel]);

  const handleAssessmentChange = (option) => {
    setFormData((prev) => ({
      ...prev,
      assessmentId: option?.value,
      educationLevelDivisionId: option?.educationLevelDivisionId,
    }));

    const studentParams = {
      assessmentId: option?.value,
      moduleCode: arrLevel,
      classroomId: formData?.classroomId,
      yearLevelId: formData?.yearLevelId,
    };
    getAllStudents(studentParams);

    const finalizedParams = {
      assessmentId: option?.value,
      classroomId: formData?.classroomId,
      yearLevelId: formData?.yearLevelId,
    };

    refetchFinalized(finalizedParams);
  };

  const handleFinalizedAssessment = () => {
    const originalIsFinalized = finalized?.[0]?.isFinalized ?? false;

    if (
      !formData?.assessmentId ||
      !formData?.classroomId ||
      !formData?.yearLevelId
    )
      return;

    const params = {
      assessmentId: formData?.assessmentId,
      classroomId: formData?.classroomId,
      isFinalized: !originalIsFinalized,
      yearLevelId: formData?.yearLevelId,
    };

    updateAssessmentFinalized(params);
  };

  const checkPermissons = () => {
    setPermissions({
      finalizeGeneralPermission:
        checkRolePermissions("finalize-general-assessment", "activity", name) ||
        checkSubsTeacherPermissions(
          "finalize-general-assessment",
          "activity",
          name,
        ),
      unFinalizeGeneralPermission:
        checkRolePermissions(
          "unfinalize-general-assessment",
          "activity",
          name,
        ) ||
        checkSubsTeacherPermissions(
          "unfinalize-general-assessment",
          "activity",
          name,
        ),
    });
  };

  useEffect(() => {
    checkPermissons();
    setFormData({});
    setStudents([]);
  }, [arrLevel]);

  return (
    <>
      <div className="arr-content">
        <div className="arr-heading">
          <h1>General Assessment</h1>
          <div className="arr-btns-group">
            <PermissionButton
              moduleName={"Recording"}
              widgetName={name}
              header={"General Assessment"}
              activityName={"select-general-assessment"}
            />
          </div>
        </div>

        <form style={{ marginBottom: "24px" }} className="arr-form grid">
          <Select
            value={
              yearLevelOptions?.find(
                (opt) => opt.value === formData.yearLevelId,
              ) || null
            }
            options={yearLevelOptions}
            onChange={(option) => {
              setFormData({
                yearLevelId: option?.value,
                educationLevelId: option?.educationLevelId,
              });
              setStudents([]);
              getClassrooms(option?.value);
            }}
            isLoading={yearLoading}
            placeholder={"Select Class"}
            isClearable={false}
          />

          <Select
            value={
              classrooms?.find((opt) => opt.value === formData.classroomId) ||
              null
            }
            options={classrooms}
            isLoading={classroomLoading}
            onChange={(option) => {
              setFormData((prev) => ({ ...prev, classroomId: option?.value }));
              setStudents([]);
              const params = {
                classroomId: option?.value,
                yearLevelId: formData?.yearLevelId,
                educationLevelId: formData?.yearLevelId,
              };
              generalAssessmentSelectByYearAndClass(params);
            }}
            isDisabled={!formData?.yearLevelId}
            placeholder={"Select Section"}
            isClearable={false}
          />

          <Select
            value={
              assessments?.find((opt) => opt.value === formData.assessmentId) ||
              null
            }
            options={assessments}
            onChange={(option) => handleAssessmentChange(option)}
            isLoading={assessmentsLoading}
            isDisabled={!formData?.classroomId}
            isClearable={false}
            placeholder={"Select Assessments"}
          />
        </form>

        <div className="arr-table-container">
          <table className="arr-table">
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Name</th>
                <th>Roll Number</th>
                <th>Registration Number</th>
                <th>Symbol Number</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {isFetchingStudents ? (
                <tr>
                  <td colSpan={6}>
                    <div className="arr-spinner">
                      <div className="loader">
                        <Spinner size={"md"} color="primary"></Spinner>
                      </div>
                      <div style={{ fontSize: "13px", marginTop: "15px" }}>
                        Please wait !! Processing.....
                      </div>
                    </div>
                  </td>
                </tr>
              ) : students?.length > 0 ? (
                students?.map((student, index) => (
                  <tr key={student.id}>
                    <td>{index + 1}</td>
                    <td>{student?.name}</td>
                    <td>{student?.rollNumber ?? "N/A"}</td>
                    <td>{student?.registrationNumber ?? "N/A"}</td>
                    <td>{student?.symbolNo ?? "N/A"}</td>
                    <td>
                      <Button
                        onClick={() => {
                          setAssessmentModal(true);
                          setSelectedStudentId(student?.id);
                          const params = {
                            ...formData,
                            admissionId: student?.id,
                          };
                          selectGeneralRecordedAssessment(params);
                        }}
                        size="sm"
                      >
                        Assessment Record
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center" }}>
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {students?.length > 0 && (
          <div className="action-btns end">
            {permissions.finalizeGeneralPermission &&
              !finalized?.[0]?.isFinalized && (
                <Button
                  disabled={isFinalizing || !recordedAssessment?.hasPermission}
                  onClick={handleFinalizedAssessment}
                >
                  {isFinalizing ? (
                    <Spinner size={"sm"} color="white" />
                  ) : (
                    "Finalize Assessment"
                  )}
                </Button>
              )}

            {permissions.unFinalizeGeneralPermission &&
              finalized?.[0]?.isFinalized && (
                <Button
                  disabled={isFinalizing || !recordedAssessment?.hasPermission}
                  onClick={handleFinalizedAssessment}
                >
                  {isFinalizing ? (
                    <Spinner size={"sm"} color="white" />
                  ) : (
                    "Unfinalize Assessment"
                  )}
                </Button>
              )}
          </div>
        )}
      </div>

      <CustomModal
        size="md"
        isOpen={assessmentModal}
        toggle={() => setAssessmentModal(false)}
        modalHeader={"General Assessment"}
        children={
          <AssessmentConfigModal
            assessmentDetail={formData}
            recordedAssessment={recordedAssessment}
            recordedAssessmentLoading={recordedAssessmentLoading}
            saveGeneralRecordedAssessment={saveGeneralRecordedAssessment}
            selectGeneralRecordedAssessment={selectGeneralRecordedAssessment}
            refetchFinalized={refetchFinalized}
            admissionId={selectStudentId}
            finalized={finalized?.[0]?.isFinalized ?? false}
            closeModal={() => setAssessmentModal(false)}
            widgetName={name}
          />
        }
      />
    </>
  );
};

export default GeneralAssessment;
